import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";

interface AddonsSectionProps {
  onBuyAddon: (addonType: string, price: number) => void;
  loading: boolean;
  activeButton: string | null;
}

export function AddonsSection({ onBuyAddon, loading, activeButton }: AddonsSectionProps) {
  const { usage } = useSubscription();
  const isPaused = usage?.is_paused;

  // Check which addons are already active
  const retentionDays = usage?.retentionDays || 30;
  const has90DayStorage = retentionDays === 90;
  const has1YearStorage = retentionDays === 365;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Add-ons</h2>
      <p className="text-sm text-muted-foreground">Scale your plan with additional users and storage</p>

      {isPaused && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm font-medium text-yellow-900">Subscription Paused</p>
          <p className="text-sm text-yellow-700 mt-1">
            Resume your subscription to add more features and users.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {/* Extra User */}
        <Card className="h-full flex flex-col">
          <CardContent className="p-6 flex flex-col h-full space-y-4">
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-left">Extra User</h3>
              <p className="text-sm text-muted-foreground text-left mt-2">
                Add team members to your workspace
              </p>
            </div>
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <div className="text-2xl font-bold">
                  $49.99<span className="text-sm font-normal text-muted-foreground">/month</span>
                </div>
                <p className="text-xs text-muted-foreground">Per additional user</p>
              </div>
              <Button
                onClick={() => onBuyAddon('extra_user', 49.99)}
                disabled={isPaused || (loading && activeButton === 'addon-extra_user')}
                className="w-full"
              >
                {activeButton === 'addon-extra_user' && loading ? 'Processing...' : isPaused ? 'Unavailable (Paused)' : 'Add User'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 90 Day Storage */}
        <Card className="h-full flex flex-col">
          <CardContent className="p-6 flex flex-col h-full space-y-4">
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-left">90 Day Storage</h3>
              <p className="text-sm text-muted-foreground text-left mt-2">
                Save your videos for 90 days from the day they were filmed
              </p>
            </div>
            <div className="space-y-3">
              <div className="text-2xl font-bold">
                {has90DayStorage ? (
                  <span className="text-green-600">Added</span>
                ) : (
                  <>$7.50<span className="text-sm font-normal text-muted-foreground">/month</span></>
                )}
              </div>
              <Button
                onClick={() => onBuyAddon('storage_90_days', 7.5)}
                disabled={has90DayStorage || isPaused || (loading && activeButton === 'addon-storage_90_days')}
                className="w-full"
                variant={has90DayStorage ? "secondary" : "default"}
              >
                {has90DayStorage ? 'Added' : activeButton === 'addon-storage_90_days' && loading ? 'Processing...' : isPaused ? 'Unavailable (Paused)' : has1YearStorage ? 'Downgrade' : 'Add Storage'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 1 Year Storage */}
        <Card className="h-full flex flex-col">
          <CardContent className="p-6 flex flex-col h-full space-y-4">
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-left">1 Year Storage</h3>
              <p className="text-sm text-muted-foreground text-left mt-2">
                Save your videos for 365 days from the day they were filmed
              </p>
            </div>
            <div className="space-y-3">
              <div className="text-2xl font-bold">
                {has1YearStorage ? (
                  <span className="text-green-600">Added</span>
                ) : (
                  <>$5.00<span className="text-sm font-normal text-muted-foreground">/month</span></>
                )}
              </div>
              <Button
                onClick={() => onBuyAddon('storage_1_year', 5.00)}
                disabled={has1YearStorage || isPaused || (loading && activeButton === 'addon-storage_1_year')}
                className="w-full"
                variant={has1YearStorage ? "secondary" : "default"}
              >
                {has1YearStorage ? 'Added' : activeButton === 'addon-storage_1_year' && loading ? 'Processing...' : isPaused ? 'Unavailable (Paused)' : has90DayStorage ? 'Upgrade' : 'Add Storage'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
